import { useQuery } from "@tanstack/react-query";

import { fetchDataGet } from "@/core/api/api-methods";
import {
  DEFAULT_PAGE,
  DEFAULT_PAGE_SIZE,
  normalizePaged,
  type ListQueryParams,
  type Paged,
} from "@/core/api/pagination";
import { QUERY_KEYS } from "@/core/api/query-keys";
import { merchantEndpoints } from "@/features/merchant/api/endpoints";
import type { MerchantDto } from "@/features/merchant/api/dto";

/**
 * Sous-marchands d'un master
 * (`GET /Merchants/{masterId}/sub-merchants?page=&pageSize=` — réponse
 * `Paged`). Query optionnel `actingMerchantId` (merchant appelant en
 * self-service).
 */
export const useSubMerchants = (
  masterId: string,
  params: ListQueryParams & { actingMerchantId?: string } = {},
  enabled = true
) => {
  const page = params.page ?? DEFAULT_PAGE;
  const pageSize = params.pageSize ?? DEFAULT_PAGE_SIZE;
  const { actingMerchantId } = params;
  return useQuery({
    queryKey: QUERY_KEYS.MERCHANT_SUB_MERCHANTS(masterId, {
      ...params,
      page,
      pageSize,
    }),
    queryFn: async () => {
      const queryParams: Record<string, string> = {
        ...params.filters,
        page: String(page),
        pageSize: String(pageSize),
      };
      if (actingMerchantId) queryParams.actingMerchantId = actingMerchantId;
      const data = await fetchDataGet<Paged<MerchantDto> | MerchantDto[] | MerchantDto>({
        url: merchantEndpoints.merchants.subMerchants(masterId),
        queryParams,
      });
      return normalizePaged(data, pageSize);
    },
    retry: (failureCount, error) =>
      !/401/.test(error?.message ?? "") && failureCount < 1,
    enabled: enabled && Boolean(masterId),
  });
};
